import { createTheme } from '@mantine/core';
import { IconCalendar } from '@tabler/icons-react';
import { createElement } from 'react';
import { Inter } from 'next/font/google';

const inter = Inter({ subsets: ['latin'], fallback: ['sans-serif'] });

/**
 * The three surface levels, and nothing else. Page is the ground, a section is a region on it, an
 * object is a thing inside a region — a border marks a region, a tint marks what is inside it. Break
 * that once (a bordered card inside a bordered card) and the page goes flat, because nothing says
 * which is the container.
 *
 * Radii and paddings are in px because they are read off the signed-off artboards and have no Mantine
 * equivalent — spacing `lg` is 20px, but `md` is 16 where the design asks for 18. Deliberately not
 * `rem()`: these must not scale with `--mantine-scale`, or a card's inset stops matching the table
 * inside it.
 */
const SURFACE = {
	radius: { section: 9, object: 6 },
	padding: {
		inline: 20,
		block: 18,
		/** The header's own inset, tighter than the body's: it is one line, not a block of content. */
		header: 15,
		footer: 14,
	},
	/*
	 * Two grounds, and which one a page gets follows from whether it has regions to separate. The app
	 * sits on white. A page built out of section cards sits on gray.1, so the cards have something to
	 * be regions *on*. A page whose whole content is one table has no regions, so it stays on the app
	 * ground and its table keeps its own boundary.
	 *
	 * That split is not ours: Cloudscape names it `full-page` versus `container` ("use this variant
	 * when the table is the entire content of a page"), and GitHub marks its issue list with a 1px
	 * border while leaving the page behind it untinted. Neither ships a card on a tinted ground for a
	 * page that is only a table.
	 *
	 * `headerFill` is Carbon's layer-relative rule: one step from the surface the table sits on. On the
	 * white app ground that step is gray.1; `PageGround` drops it, because on the section ground the
	 * step *is* the ground's own colour, so a fill would read as a hole punched through the card.
	 */
	light: {
		pageBg: 'var(--mantine-color-white)',
		detailBg: 'var(--mantine-color-gray-1)',
		sectionBg: 'var(--mantine-color-white)',
		sectionBorder: 'var(--mantine-color-gray-3)',
		objectBg: 'var(--mantine-color-gray-0)',
		rule: 'var(--mantine-color-gray-2)',
		headerFill: 'var(--mantine-color-gray-1)',
		headerColor: 'var(--mantine-color-gray-7)',
	},
	/*
	 * The app ships light only today, so these are the same relationships one step darker rather than a
	 * designed dark palette — enough that a section stays a region and an object stays a tint if the
	 * scheme is ever switched on, and a single place to correct when it is designed for real.
	 */
	dark: {
		pageBg: 'var(--mantine-color-dark-7)',
		detailBg: 'var(--mantine-color-dark-8)',
		sectionBg: 'var(--mantine-color-dark-7)',
		sectionBorder: 'var(--mantine-color-dark-4)',
		objectBg: 'var(--mantine-color-dark-6)',
		rule: 'var(--mantine-color-dark-4)',
		headerFill: 'var(--mantine-color-dark-6)',
		headerColor: 'var(--mantine-color-dark-1)',
	},
} as const;

export type SurfaceScale = typeof SURFACE;

/** Types `theme.other.surface`, so the resolver and any future reader are checked against the scale. */
declare module '@mantine/core' {
	export interface MantineThemeOther {
		surface: SurfaceScale;
	}
}

export const theme = createTheme({
	fontFamily: inter.style.fontFamily,
	headings: {
		fontFamily: `${inter.style.fontFamily}`,
	},
	spacing: {
		xs: '.5rem',
	},
	colors: {
		weinvest: [
			'#f4f8fb',
			'#ddebf9',
			'#b7d5f1',
			'#8ab5dd',
			'#5c89b4',
			'#446f97',
			'#365e84',
			'#254562',
			'#22374f',
			'#172C40',
			'#122333',
		],
		turquoise: [
			'#e9fcfc',
			'#cdfafb',
			'#a5f7fa',
			'#65ecf0',
			'#24e0e5',
			'#21cfd3',
			'#1892a2',
			'#027686',
			'#194f5f',
			'#124453',
		],
	},
	/*
	 * Two rungs deeper than it was. White on the old light stop `orange.5 #ff922b` measured 2.23:1 —
	 * below even the 3:1 floor a button's icon needs, let alone the 4.5:1 its 14px label needs. This
	 * puts the worst case at `orange.7 #f76707` = 3.04:1 and the best at `orange.9 #d9480f` = 4.30:1.
	 *
	 * That clears the non-text floor and is a large improvement, but it is **not AA for the label**:
	 * white text needs a background at least as dark as ~#c2410c, which is past the brand's orange.
	 * Recorded here rather than quietly worked around — it is a brand decision, not a CSS one.
	 */
	defaultGradient: {
		from: 'var(--mantine-color-orange-7)',
		to: 'var(--mantine-color-orange-9)',
		deg: 45,
	},
	components: {
		Container: {
			defaultProps: {
				size: '64rem',
				w: '100%',
				p: 'lg',
			},
		},
		Loader: {
			defaultProps: {
				color: 'turquoise.4',
			},
		},
		/*
		 * Every date picker in the app shows DD/MM/YYYY and a calendar icon. Here rather than in a wrapper,
		 * so a picker cannot lose them by being written without it.
		 */
		DatePickerInput: {
			defaultProps: {
				valueFormat: 'DD/MM/YYYY',
				rightSection: createElement(IconCalendar, { size: 18, stroke: 1.5 }),
			},
		},
		Badge: {
			defaultProps: {
				// Mantine uppercases badges; the design does not. Set here, this deletes nine copies of
				// `style={{ textTransform: 'none' }}` scattered through the admin.
				tt: 'none',
			},
		},
	},
	/*
	 * 8px on every control, instead of Mantine's stock 4.
	 *
	 * The 4 was never a decision — it is what you get by not setting this — and the app had already
	 * voted against it forty-one times over, writing `radius="md"` at one call site after another.
	 * A value written at three or more call sites is a default, not a habit, so it lives here and
	 * those props are gone.
	 *
	 * It leaves the surface scale alone: `Section` takes its 9px from `--surface-section-radius` in
	 * its own stylesheet, so a card stays a hair rounder than the controls inside it — the container
	 * is still the roundest thing on the page. Pills (`xl`) and skeleton text lines (`sm`) pass their
	 * own radius and are unaffected.
	 */
	defaultRadius: 'md',
	primaryColor: 'weinvest',
	/*
	 * Mantine's slot for values it has no field of its own for. `surface.variables.ts` projects these
	 * into CSS custom properties for the stylesheets that consume them — the values themselves belong
	 * here, so this file stays the one place to read the project's scale.
	 */
	other: {
		surface: SURFACE,
	},
});
